import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const DeleteConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Confirmer la suppression",
  message = "Êtes-vous sûr de vouloir supprimer cet élément ?",
  itemName = "",
  confirmText = "Supprimer",
  requiresConfirmation = false
}) => {
  const { isDarkMode } = useTheme();
  const [confirmationText, setConfirmationText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (requiresConfirmation && confirmationText !== itemName) {
      return;
    }

    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
      setConfirmationText('');
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    onClose();
  };

  if (!isOpen) return null;

  const canConfirm = !requiresConfirmation || confirmationText === itemName;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isDarkMode ? 'bg-gray-900 border border-gray-700 text-gray-100' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-md overflow-hidden`}>
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-900 border-b border-gray-700' : 'bg-gradient-to-r from-red-600 to-red-700'} px-6 py-4`}>
          <div className="flex items-center">
            <div className={`w-12 h-12 ${isDarkMode ? 'bg-gray-800' : 'bg-white bg-opacity-20'} rounded-xl flex items-center justify-center mr-4`}>
              <i className={`fas fa-exclamation-triangle ${isDarkMode ? 'text-red-400' : 'text-white'} text-xl`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{title}</h2>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-red-100'} text-sm`}>Cette action est irréversible</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning Message */}
          <div className={`mb-6 ${isDarkMode ? 'bg-gray-800 border-l-4 border-l-red-700' : 'bg-amber-50 border-l-4 border-l-amber-500'} p-4 rounded-r-lg`}>
            <div className="flex">
              <i className={`fas fa-exclamation-triangle mr-2 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-amber-500'}`}></i>
              <div>
                <h4 className={`font-medium mb-1 ${isDarkMode ? 'text-red-300' : 'text-amber-800'}`}>Attention</h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-amber-700'}`}>{message}</p>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          {requiresConfirmation && itemName && (
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Pour confirmer, tapez <span className="font-semibold text-red-600">"{itemName}"</span>
              </label>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-400' : 'border-gray-300'}`}
                placeholder={`Tapez "${itemName}" pour confirmer`}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-200'} px-6 py-4`}>
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className={`px-6 py-3 rounded-lg transition-colors font-medium disabled:opacity-50 ${
                isDarkMode
                  ? 'text-gray-200 bg-gray-900 border border-gray-700 hover:bg-gray-800'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !canConfirm}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? 'bg-red-700 text-white hover:bg-red-800'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <i className="fas fa-trash mr-2"></i>
                  {confirmText}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;